import { NotificationResponseModel } from '../../../../types/NotificationModels';
// import { Avatar, Divider, ListItem, ListItemText } from "@mui/material";
import { formatDistanceToNow, parseISO } from 'date-fns';
import { getProfilePicture } from '../../../../types/UserDocument';
import { Card, Group, Text, Avatar, Badge, Stack } from '@mantine/core';
import { primaryColor } from '../../../../constants/theme';
import {
  IconCalendar,
  IconCalendarQuestion,
  IconCheck,
  IconClock,
  IconX,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import classes from './Notification.module.css';
import { getNotificationLink } from './notificationUtils';
import clsx from 'clsx';

const isBookingRequestNotification = (notification: NotificationResponseModel): boolean =>
  notification.notificationTypeString === 'BookingRequested' ||
  notification.title.toLowerCase().includes('booking request');

const getBookingIdsFromNotification = (notification: NotificationResponseModel): number[] => {
  try {
    const parsedData = JSON.parse(notification.messageData);
    const ids: unknown[] =
      parsedData?.BookingIds ?? parsedData?.bookingIds ?? (Array.isArray(parsedData) ? parsedData : []);
    return (ids as (string | number)[])
      .map((id) => parseInt(String(id), 10))
      .filter((id) => !Number.isNaN(id) && id > 0);
  } catch {
    return [];
  }
};

export const RenderNotification = ({
  notification,
  onBookingRequest,
}: {
  notification: NotificationResponseModel;
  onBookingRequest?: (ids: number[]) => void;
}) => {
  const navigate = useNavigate();
  const link = getNotificationLink(notification);
  const isActionable = !!link;

  const hasConfirmed = notification.message.includes('confirmed');
  const hasDeclined = notification.message.includes('rejected');
  const hasCancelled = notification.message.includes('cancelled');
  const hasRequested = notification.message.includes('requested');
  const isAvailabilityRequested =
    notification.notificationTypeString === 'AvailabilityRequested';
  const isPromptAvailabilityRequested =
    notification.notificationTypeString === 'PromptAvailabilityRequested';

  const notificationTypeIcon = isAvailabilityRequested ? (
    <IconClock
      size={24}
      color={primaryColor}
      data-testid={`notification-icon-clock-${notification.id}`}
    />
  ) : isPromptAvailabilityRequested ? (
    <IconCalendar
      size={24}
      color={primaryColor}
      data-testid={`notification-icon-calendar-${notification.id}`}
    />
  ) : null;

  const handleClick = () => {
    if (onBookingRequest && isBookingRequestNotification(notification)) {
      const ids = getBookingIdsFromNotification(notification);
      if (ids.length > 0) {
        onBookingRequest(ids);
        return;
      }
    }
    if (link) {
      navigate(link);
    }
  };

  return (
    <Card
      padding="lg"
      radius="md"
      withBorder
      onClick={handleClick}
      data-testid={`notification-${notification.id}`}
      className={clsx(classes.card, { [classes.nonActionable]: !isActionable })}
    >
      <Stack align="end">
        <Group w="100%" gap="md" align="center">
          <Avatar
            h={48}
            w={48}
            src={notificationTypeIcon ? undefined : getProfilePicture(notification.encodedImage)}
            data-testid={`notification-avatar-${notification.id}`}
            style={
              notificationTypeIcon
                ? { backgroundColor: 'rgba(0, 202, 154, 0.12)' }
                : undefined
            }
          >
            {notificationTypeIcon}
          </Avatar>
          <Text style={{ flex: 1 }} ta="start">
            {notification.message}
          </Text>
        </Group>
        <Group gap={5}>
          {hasConfirmed && (
            <Badge
              color={primaryColor}
              variant="outline"
              leftSection={<IconCheck size={15} />}
              radius={'sm'}
            >
              Confirmed
            </Badge>
          )}
          {hasDeclined && (
            <Badge
              color={'red'}
              variant="outline"
              leftSection={<IconX size={15} />}
              radius={'sm'}
            >
              Rejected
            </Badge>
          )}
          {hasCancelled && (
            <Badge
              color={'red'}
              variant="outline"
              leftSection={<IconX size={15} />}
              radius={'sm'}
            >
              Cancelled
            </Badge>
          )}
          {hasRequested && (
            <Badge
              color={'blue'}
              variant="outline"
              leftSection={<IconCalendarQuestion size={15} />}
              radius={'sm'}
            >
              Requested
            </Badge>
          )}

          <Badge color={primaryColor} variant="outline" radius={'sm'}>
            {formatDistanceToNow(parseISO(notification.createdIso), {
              addSuffix: true,
            })}
          </Badge>
        </Group>
      </Stack>
    </Card>
  );
};
